import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonFastAPI = () => {
    return (
        <div className="tutorial-content-module">
            <div className="premium-header">
                <h1>Building APIs with FastAPI</h1>
                <p className="subtitle">The Ferrari of Python Web Frameworks. High performance, auto-documented, and type-safe.</p>
            </div>

            <div className="content-section">
                <h2>1. The Big Idea (ELI5)</h2>
                <ProTip><p>
                        Old frameworks (like Flask/Django) are like a <strong>Traditional Butler</strong>. They take one order, walk to the kitchen, wait for the food, and bring it back. They can only serve one person at a time per thread.
                    </p>
                    <p>
                        <strong>FastAPI</strong> is like a <strong>Waiter with a Tablet</strong> using <strong>Async IO</strong>.
                    </p>
                    <ul>
                        <li><strong>Async:</strong> They take an order, hit "Send to Kitchen", and <em>immediately</em> go to the next table while the kitchen cooks. They don't wait.</li>
                        <li><strong>Type Hints:</strong> The Tablet forces the customer to pick valid options. You literally <em>cannot</em> order a "Purple Hamburger" because the menu (Schema) rejects it instantly.</li>
                    </ul></ProTip>
            </div>

            <div className="content-section">
                <h2>2. The Syntax: Types are NOT Optional</h2>
                <p>
                    FastAPI uses Python's modern Type Hints to do three things at once: <strong>Validate Data</strong>, <strong>Convert Types</strong> (Serialization), and <strong>Generate Documentation</strong>.
                </p>
                <CodeBlock language="python" code={`from fastapi import FastAPI

app = FastAPI()

# Input Validation: 'item_id' MUST be an int
# Output Documentation: Returns a Dictionary
@app.get("/items/{item_id}")
async def read_item(item_id: int, q: str | None = None):
    return {"item_id": item_id, "query": q}

# Calling /items/foo returns a standard 422 JSON Error:
# "value is not a valid integer"`} />
            </div>

            <div className="content-section">
                <h2>3. Pydantic: The Data Guardian</h2>
                <p>
                    For complex data (like a User Registration), we define a <strong>Schema</strong> using Pydantic. This is your first line of defense against bad data.
                </p>
                <CodeBlock language="python" code={`from pydantic import BaseModel, EmailStr

class UserSignup(BaseModel):
    username: str
    email: EmailStr # Validates explicitly as email
    age: int
    is_premium: bool = False

@app.post("/users/")
async def create_user(user: UserSignup):
    # If we get here, 'user' is GUARANTEED to be valid.
    # No need for manual checks like "is email valid?".
    
    # We can access data as attributes
    if user.age < 18:
        return {"error": "Too young"}
        
    return {"message": f"Welcome {user.username}!"}`} />
            </div>

            <div className="content-section">
                <h2>4. The Magic: Automatic Interactive Docs</h2>
                <p>
                    Because you used Type Hints, FastAPI writes the manual for you.
                    Navigate to <code>/docs</code> to see a full <strong>Swagger UI</strong>. You can test your API endpoints directly from the browser without writing a single line of HTML.
                </p>
            </div>

            <div className="content-section">
                <h2>5. Deep Dive: Dependency Injection (`Depends`)</h2>
                <p>
                    This is FastAPI's "Killer Feature". It lets you share logic (like Auth or DB connections) across thousands of endpoints cleanly.
                </p>
                <CodeBlock language="python" code={`from fastapi import Depends, HTTPException

# 1. logical function to get current user
async def get_current_user(token: str):
    if token != "secret-token":
        raise HTTPException(status_code=401, detail="Unauthorized")
    return {"user": "admin"}

# 2. Inject it into the route
# The route CODE will only run if dependencies succeed
@app.get("/dashboard")
async def dashboard(user: dict = Depends(get_current_user)):
    return {"message": "Secret Data", "user": user}`} />
            </div>

            <div className="content-section">
                <h2>6. Deployment: The Uvicorn Engine</h2>
                <p>
                    FastAPI is just the framework. To run it in production, you need an <strong>ASGI Server</strong>. The standard is <strong>Uvicorn</strong> (Lightning Fast).
                </p>
                <CodeBlock language="bash" code={`# Development (Auto-Reload)
uvicorn main:app --reload

# Production (Multiple Workers)
gunicorn main:app -w 4 -k uvicorn.workers.UvicornWorker`} />
            </div>

        </div>
    );
};

export default PythonFastAPI;

